'use no memo';

import { useClickOutside } from '@mantine/hooks';
import { useLocation } from '@tanstack/react-router';
import type { EditorContentProps } from '@tiptap/react';
import { EditorContent } from '@tiptap/react';
import { useEffect, useState } from 'react';
import { Group } from '#core/components';
import { useCombobox, useComboboxKeyHandler } from '../combobox';
import { ClearButton, SearchIcon } from './components';
import { useCurrentEditor } from './hooks';

import classes from './TextEditor.module.css';

type Props = Omit<EditorContentProps, 'editor'>;

export const TextEditor = (props: Props) => {
  const { search } = useLocation();
  const { editor } = useCurrentEditor();
  const { state } = useCombobox();
  const { handleKeyDown } = useComboboxKeyHandler(editor);

  const [isEmpty, setIsEmpty] = useState(!search.q);

  const ref = useClickOutside(() => {
    state.close();
  });

  const handleFocus = () => {
    if (!isEmpty) {
      state.open();
      return;
    }

    ref.current?.addEventListener('transitionend', state.open, {
      once: true
    });
  };
  const handleBlur = () => state.close();

  useEffect(() => {
    if (!editor) return;

    const updateEmpty = () => setIsEmpty(editor.isEmpty);

    editor.on('update', updateEmpty);
    updateEmpty();

    return () => {
      editor.off('update', updateEmpty);
    };
  }, [editor]);

  return (
    <Group className={classes.group} data-editor-empty={isEmpty} ref={ref}>
      <SearchIcon />
      <EditorContent
        {...props}
        className={classes.editor}
        editor={editor}
        onBlur={handleBlur}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
      />
      <ClearButton />
    </Group>
  );
};
